/**
 * Employee Profile Print Page
 *
 * Print-optimized page showing employee overview with:
 * - Employee details (name, function, team, level, total points)
 * - Tasks grouped with their linked competencies
 * - Basis competencies (applicable to all tasks)
 * - Voldoende criteria for each competency
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintLayout } from '../../components/print';
import { employeesApi, type EmployeePrintData, type PrintTask, type PrintCompetency } from '../../lib/employeesApi';

/**
 * Nummert uitsluitend de ACTIEVE taken doorlopend; een gearchiveerde taak krijgt
 * geen nummer (`null`) en wordt alleen bij naam genoemd.
 *
 * Zou je over de complete lijst doornummeren, dan verschijnt op papier
 * "Taak 3: ... (Gearchiveerd)" bóven "Aantal taken: 2" -- de kop belooft dan een
 * derde taak die de samenvatting eronder ontkent. De nummering moet dezelfde
 * verzameling beschrijven als `summary.aantalTaken`, en die telt actief-only.
 * Spec: _bmad-output/implementation-artifacts/spec-weergave-actief-puntentotaal.md
 */
function nummerActieveTaken(taken: PrintTask[]): Array<{ task: PrintTask; nummer: number | null }> {
  let actiefTeller = 0;
  return taken.map((task) => ({
    task,
    nummer: task.isActief === false ? null : ++actiefTeller,
  }));
}

interface TaskBlockProps {
  task: PrintTask;
  /** Volgnummer binnen de actieve taken, of `null` voor een gearchiveerde taak. */
  nummer: number | null;
}

function TaskBlock({ task, nummer }: TaskBlockProps) {
  // Een gearchiveerde taak blijft op papier staan, mét haar competenties -- die
  // koppeling is feitelijk juist en leegmaken zou de terugval "Geen
  // vakcompetenties gekoppeld aan deze taak" afdrukken, wat onwaar is. Alleen
  // de samenvatting eronder telt hem niet mee, en dat moet zichtbaar zijn.
  // Spec: _bmad-output/implementation-artifacts/spec-weergave-actief-puntentotaal.md
  const isGearchiveerd = task.isActief === false;

  return (
    <div
      data-testid={`print-task-block-${task.id}`}
      className={`print-card mb-6 border rounded-lg overflow-hidden ${
        isGearchiveerd ? 'border-gray-400 border-dashed' : 'border-gray-300'
      }`}
    >
      {/* Task Header */}
      <div
        className={`border-b px-4 py-3 flex justify-between items-center gap-2 ${
          isGearchiveerd ? 'bg-gray-100 border-gray-300' : 'bg-green-50 border-green-200'
        }`}
      >
        <h3
          className={`text-base font-bold ${isGearchiveerd ? 'text-gray-600' : 'text-green-800'}`}
        >
          {nummer !== null ? `Taak ${nummer}: ${task.name}` : task.name}
          {isGearchiveerd && (
            <span className="ml-2 text-xs font-semibold text-gray-600 bg-gray-200 px-2 py-0.5 rounded-full whitespace-nowrap">
              Gearchiveerd &ndash; telt niet mee
            </span>
          )}
        </h3>
        <span
          className={`text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
            isGearchiveerd
              ? 'text-gray-500 bg-gray-200 line-through'
              : 'text-green-700 bg-green-100'
          }`}
        >
          {task.weging} punten
        </span>
      </div>

      {/* Task Content */}
      <div className="p-4 space-y-4">
        {/* Beschrijving */}
        {task.beschrijving && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Omschrijving</p>
            <p className="text-sm text-gray-700">{task.beschrijving}</p>
          </div>
        )}

        {/* Voldoende criterium */}
        {task.voldoendeCriterium && (
          <div className="mt-2 bg-green-50 border-l-2 border-green-500 pl-3 py-2">
            <p className="text-sm text-green-800">
              <span className="font-semibold">&#10003; Voldoende:</span> "{task.voldoendeCriterium}"
            </p>
          </div>
        )}

        {/* Voorbeelden */}
        {task.examples && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Voorbeelden</p>
            <p className="text-sm text-gray-600">{task.examples}</p>
          </div>
        )}

        {/* Competencies for this task */}
        {task.competencies.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">
              Competenties bij deze taak
            </p>
            <div className="space-y-3">
              {task.competencies.map((comp) => (
                <CompetencyItem key={comp.id} competency={comp} />
              ))}
            </div>
          </div>
        )}

        {task.competencies.length === 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-400 italic">
              Geen vakcompetenties gekoppeld aan deze taak
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface CompetencyItemProps {
  competency: PrintCompetency;
}

function CompetencyItem({ competency }: CompetencyItemProps) {
  return (
    <div className="print-row bg-gray-50 rounded-lg p-3">
      <div className="flex items-start gap-2">
        <span className="text-orange-500 mt-0.5">&#9679;</span>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">{competency.name}</p>
          {competency.beschrijving && (
            <p className="text-xs text-gray-600 mt-1">{competency.beschrijving}</p>
          )}
          {competency.voldoendeCriterium && (
            <div className="mt-2 bg-green-50 border-l-2 border-green-500 pl-3 py-1">
              <p className="text-xs text-green-800">
                <span className="font-semibold">&#10003; Voldoende:</span> "{competency.voldoendeCriterium}"
              </p>
            </div>
          )}
          {competency.examples && (
            <p className="text-xs text-gray-500 mt-2">
              <span className="font-medium">Voorbeelden:</span> {competency.examples}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

interface BasisCompetenciesSectionProps {
  competencies: PrintCompetency[];
}

function BasisCompetenciesSection({ competencies }: BasisCompetenciesSectionProps) {
  if (competencies.length === 0) return null;

  return (
    <div className="print-section mb-6">
      <div className="border border-purple-300 rounded-lg overflow-hidden">
        <div className="bg-purple-50 border-b border-purple-200 px-4 py-3">
          <h3 className="text-base font-bold text-purple-800 flex items-center gap-2">
            <span>&#9733;</span>
            Basiscompetenties
            <span className="text-xs font-normal text-purple-600">(voor alle taken)</span>
          </h3>
        </div>
        <div className="p-4 space-y-3">
          {competencies.map((comp) => (
            <div key={comp.id} className="print-row bg-purple-50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <span className="text-purple-500 mt-0.5">&#9679;</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{comp.name}</p>
                  {comp.beschrijving && (
                    <p className="text-xs text-gray-600 mt-1">{comp.beschrijving}</p>
                  )}
                  {comp.voldoendeCriterium && (
                    <div className="mt-2 bg-green-50 border-l-2 border-green-500 pl-3 py-1">
                      <p className="text-xs text-green-800">
                        <span className="font-semibold">&#10003; Voldoende:</span> "{comp.voldoendeCriterium}"
                      </p>
                    </div>
                  )}
                  {comp.examples && (
                    <p className="text-xs text-gray-500 mt-2">
                      <span className="font-medium">Voorbeelden:</span> {comp.examples}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface SummarySectionProps {
  summary: EmployeePrintData['summary'];
}

function SummarySection({ summary }: SummarySectionProps) {
  return (
    <div className="print-section mt-6 pt-4 border-t-2 border-gray-300">
      <div className="bg-blue-50 rounded-lg p-4">
        <h3 className="text-sm font-bold text-blue-800 mb-3">Samenvatting</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Aantal taken</p>
            <p className="font-semibold text-gray-900">{summary.aantalTaken}</p>
          </div>
          <div>
            <p className="text-gray-500">Totaal punten</p>
            <p className="font-semibold text-gray-900">{summary.totaalPunten}</p>
          </div>
          <div>
            <p className="text-gray-500">Vakcompetenties</p>
            <p className="font-semibold text-gray-900">{summary.aantalVakCompetenties}</p>
          </div>
          <div>
            <p className="text-gray-500">Basiscompetenties</p>
            <p className="font-semibold text-gray-900">{summary.aantalBasisCompetenties}</p>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-blue-200">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Huidig niveau</span>
            <span className="font-bold text-blue-800 text-lg">{summary.niveau}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EmployeeProfilePrintPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const employeeId = Number(id);

  const { data: printData, isLoading, error } = useQuery<EmployeePrintData>({
    queryKey: ['employee', 'print', employeeId],
    queryFn: () => employeesApi.getPrintData(employeeId),
    enabled: !isNaN(employeeId),
  });

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  return (
    <PrintLayout
      title="Functie- & Takenoverzicht"
      subtitle={printData?.employee.name}
      isLoading={isLoading}
      loadingMessage="Medewerkeroverzicht voorbereiden..."
      error={error ? 'Kon de medewerkergegevens niet laden' : undefined}
      onClose={handleClose}
      orientation="portrait"
      autoPrint={true}
      footerContent={
        <p>
          Functieloonhuis Beoordelingssysteem - Medewerker Takenoverzicht
          {printData && ` - Gegenereerd: ${new Date(printData.generatedAt).toLocaleString('nl-NL')}`}
        </p>
      }
    >
      {printData && (
        <>
          {/* Employee Info Header */}
          <div className="print-section mb-6 bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Naam</p>
                <p className="font-semibold text-gray-900">{printData.employee.name}</p>
              </div>
              <div>
                <p className="text-gray-500">Functie</p>
                <p className="font-semibold text-gray-900">{printData.employee.functie || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Team</p>
                <p className="font-semibold text-gray-900">{printData.employee.team || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Niveau</p>
                <p className="font-semibold text-gray-900">{printData.employee.niveau.name}</p>
              </div>
              <div>
                <p className="text-gray-500">Totaal punten</p>
                <p className="font-semibold text-gray-900">{printData.employee.totalPunten}</p>
              </div>
              <div>
                <p className="text-gray-500">In dienst sinds</p>
                <p className="font-semibold text-gray-900">
                  {printData.employee.startdatum
                    ? new Date(printData.employee.startdatum).toLocaleDateString('nl-NL')
                    : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Tasks with Competencies */}
          {printData.tasks.length > 0 ? (
            <div className="print-section">
              {nummerActieveTaken(printData.tasks).map(({ task, nummer }) => (
                <TaskBlock key={task.id} task={task} nummer={nummer} />
              ))}
            </div>
          ) : (
            <div className="print-section mb-6 bg-gray-50 rounded-lg p-6 text-center">
              <p className="text-gray-500">Geen taken toegewezen aan deze medewerker.</p>
            </div>
          )}

          {/* Basis Competencies */}
          <BasisCompetenciesSection competencies={printData.basisCompetencies} />

          {/* Summary */}
          <SummarySection summary={printData.summary} />
        </>
      )}
    </PrintLayout>
  );
}

export default EmployeeProfilePrintPage;
